import { useEffect, useState } from "react";

import { useSitesRepository } from "@/pages/tina/sites/hooks/use-sites-repository";

/**
 * `sites.city` marks a country-wide or every-city source rather than a place. It is
 * never a city a Maps link can be centred on.
 */
const ALL_CITIES = "all";

/**
 * The site's own city, for the venue HITL's "use this city's center" autofill.
 *
 * The button's first choice is the event's own city off `readMusicEventContext`, and
 * on a venue HITL that is never there: the extract stage writes no `city` field at
 * all (only `country`/`countryCode`), and the other two sources — the resolved
 * venue's geodata and its area — belong to a venue the HITL exists BECAUSE we do not
 * have. All 7 open venue HITLs on 2026-09-11 had none, so the button never rendered.
 *
 * The site does know: `ra.co/ger` is Berlin, `ra.co/us/los-angeles` is Los Angeles.
 * That is a city for the whole source rather than for this one event, which is
 * exactly the right granularity for a TBA venue — the curator is placing the event
 * in a city, not at an address.
 *
 * Pass `undefined` to skip the lookup (no site, or a caller that already has a city).
 */
export function useSiteCity(siteName: string | undefined): string | undefined {
  const { get } = useSitesRepository();
  const [city, setCity] = useState<string | undefined>();

  useEffect(() => {
    if (!siteName) {
      setCity(undefined);
      return;
    }
    let active = true;
    get(siteName)
      .then((site) => {
        if (!active) return;
        const name = site.city?.trim();
        setCity(name && name.toLowerCase() !== ALL_CITIES ? name : undefined);
      })
      .catch(() => active && setCity(undefined));
    return () => {
      active = false;
    };
    // `get` is rebuilt every render by the repository hook; the site name is the
    // only real input, so keying on it is what stops an endless refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteName]);

  return city;
}
